import React, { useState } from "react";
import {
	Box,
	TextField,
	Button,
	Typography,
	Paper,
	CircularProgress,
	Chip,
	InputAdornment,
	List,
	ListItemButton,
	ListItemText,
	ListItemIcon,
} from "@mui/material";
import {
	Search as SearchIcon,
	Add as AddIcon,
	CheckCircle as CheckCircleIcon,
	Block as BlockIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { zoullooColors } from "../../theme";
import { useSearchCompaniesAutocomplete } from "../../hooks/useCompany";
import { Company } from "../../models/Company";
import { CompanyGroupCompany } from "../../models/CompanyGroup";

interface CompanyGroupFormProps {
	initialData?: {
		name: string;
		website?: string;
	};
	initialCompanies?: CompanyGroupCompany[];
	currentGroupId?: number;
	onSubmit: (data: { name: string; website?: string; companyIds: number[] }) => void;
	isSubmitting: boolean;
	submitButtonText: string;
}

const CompanyGroupForm: React.FC<CompanyGroupFormProps> = ({
	initialData,
	initialCompanies = [],
	currentGroupId,
	onSubmit,
	isSubmitting,
	submitButtonText,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [name, setName] = useState(initialData?.name || "");
	const [website, setWebsite] = useState(initialData?.website || "");
	const [companies, setCompanies] = useState<CompanyGroupCompany[]>(initialCompanies);
	const [nameError, setNameError] = useState("");
	const [companySearchTerm, setCompanySearchTerm] = useState("");

	const { data: searchResults, isLoading: isSearchingCompanies } =
		useSearchCompaniesAutocomplete(companySearchTerm);

	const selectedIds = new Set(companies.map((c) => c.id));

	const handleAddCompany = (company: Company) => {
		if (selectedIds.has(company.id)) return;
		setCompanies((prev) => [
			...prev,
			{
				id: company.id,
				legalName: company.legalName,
				tradingName: company.tradingName,
				sector: company.sector,
			},
		]);
	};

	const handleRemoveCompany = (companyId: number) => {
		setCompanies((prev) => prev.filter((c) => c.id !== companyId));
	};

	const handleSubmit = () => {
		setNameError("");

		if (!name.trim()) {
			setNameError(t("companyGroup.nameRequired"));
			return;
		}

		onSubmit({
			name: name.trim(),
			website: website.trim() || undefined,
			companyIds: companies.map((c) => c.id),
		});
	};

	const handleWebsiteBlur = () => {
		if (website && !website.startsWith("http://") && !website.startsWith("https://")) {
			setWebsite(`https://${website}`);
		}
	};

	const showResults = companySearchTerm.length >= 2;

	return (
		<Box>
			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Typography variant="h4" color={zoullooColors.secondary.dark} sx={{ mb: 3 }}>
					{t("companyGroup.groupInformation")}
				</Typography>

				<Box sx={{ mb: 3 }}>
					<TextField
						fullWidth
						label={t("companyGroup.name")}
						value={name}
						onChange={(e) => {
							setName(e.target.value);
							setNameError("");
						}}
						required
						error={!!nameError}
						helperText={nameError}
						inputProps={{ maxLength: 200 }}
					/>
				</Box>

				<Box sx={{ mb: 3 }}>
					<TextField
						fullWidth
						label={t("companyGroup.website")}
						value={website}
						onChange={(e) => setWebsite(e.target.value)}
						onBlur={handleWebsiteBlur}
						placeholder="https://www.example.com"
						inputProps={{ maxLength: 255 }}
					/>
				</Box>
			</Paper>

			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Typography variant="h4" color={zoullooColors.secondary.dark} sx={{ mb: 3 }}>
					{t("companyGroup.companies")} ({companies.length})
				</Typography>

				{/* Selected companies as chips */}
				{companies.length > 0 && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
						{companies.map((company) => (
							<Chip
								key={company.id}
								label={company.legalName}
								onDelete={() => handleRemoveCompany(company.id)}
								color="primary"
								variant="outlined"
							/>
						))}
					</Box>
				)}

				{/* Search field */}
				<TextField
					fullWidth
					placeholder={t("companyGroup.searchCompanyPlaceholder")}
					value={companySearchTerm}
					onChange={(e) => setCompanySearchTerm(e.target.value)}
					size="small"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
						endAdornment: isSearchingCompanies ? (
							<InputAdornment position="end">
								<CircularProgress size={20} />
							</InputAdornment>
						) : null,
					}}
				/>

				{/* Search results */}
				{showResults && !isSearchingCompanies && searchResults && searchResults.length > 0 && (
					<List
						dense
						sx={{
							mt: 1,
							maxHeight: 250,
							overflow: "auto",
							border: `1px solid ${zoullooColors.border.main}`,
							borderRadius: 1,
						}}
					>
						{searchResults.map((company: Company) => {
							const isSelected = selectedIds.has(company.id);
							const belongsToOtherGroup = company.companyGroup
								&& company.companyGroup.id !== currentGroupId;
							const isDisabled = isSelected || !!belongsToOtherGroup;

							let icon;
							if (isSelected) {
								icon = <CheckCircleIcon sx={{ color: "green", fontSize: 20 }} />;
							} else if (belongsToOtherGroup) {
								icon = <BlockIcon sx={{ color: "text.disabled", fontSize: 20 }} />;
							} else {
								icon = <AddIcon sx={{ color: zoullooColors.secondary.main, fontSize: 20 }} />;
							}

							const primaryText = company.tradingName
								? `${company.legalName} (${company.tradingName})`
								: company.legalName;

							const secondaryParts: string[] = [];
							if (company.sector?.label) secondaryParts.push(company.sector.label);
							if (belongsToOtherGroup) {
								secondaryParts.push(
									t("companyGroup.alreadyInGroup", { groupName: company.companyGroup!.name })
								);
							}

							return (
								<ListItemButton
									key={company.id}
									onClick={() => !isDisabled && handleAddCompany(company)}
									disabled={isDisabled}
									sx={{
										"&.Mui-disabled": {
											opacity: 0.6,
										},
									}}
								>
									<ListItemIcon sx={{ minWidth: 36 }}>
										{icon}
									</ListItemIcon>
									<ListItemText
										primary={primaryText}
										secondary={secondaryParts.join(" - ")}
										secondaryTypographyProps={{
											color: belongsToOtherGroup ? "error" : "text.secondary",
										}}
									/>
								</ListItemButton>
							);
						})}
					</List>
				)}

				{showResults && !isSearchingCompanies && searchResults && searchResults.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						{t("companyGroup.noCompaniesFound")}
					</Typography>
				)}

				{!showResults && companySearchTerm.length > 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						{t("companyGroup.typeToSearch")}
					</Typography>
				)}
			</Paper>

			<Box display="flex" gap={2} justifyContent="flex-end">
				<Button
					variant="outlined"
					onClick={() => navigate(-1)}
					sx={{
						color: zoullooColors.secondary.main,
						borderColor: zoullooColors.secondary.main,
						"&:hover": {
							borderColor: zoullooColors.secondary.dark,
						},
					}}
				>
					{t("common.cancel")}
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={isSubmitting}
					sx={{
						backgroundColor: zoullooColors.secondary.main,
						"&:hover": {
							backgroundColor: zoullooColors.secondary.dark,
						},
					}}
				>
					{isSubmitting ? <CircularProgress size={20} color="inherit" /> : submitButtonText}
				</Button>
			</Box>
		</Box>
	);
};

export default CompanyGroupForm;
